import {
  GRAMMAR,
  PLAGIARISM,
  UserInteractionStates,
} from '@src/components/Cards/constants'
import { CardContent } from '@src/components/Editor/types'
import * as checkActions from '@src/state/checks/actions'
import * as documentActions from '@src/state/document/actions'
import * as sessionActions from '@src/state/session/actions'
import * as workspaceActions from '@src/state/workspace/actions'
import {
  DocumentActions,
  GlobalActions,
  SessionActions,
  WorkspaceActions,
} from '@src/types/actions'
import { CheckType } from '@src/types/checks'
import { ContentState } from 'draft-js'
import { getType } from 'typesafe-actions'
import { resetState } from '../actions'
import { CheckActions } from '../checks/reducer'
import { CheckCategory, EXPERT_CHECK } from '../document/types'

export const DEFAULT_GRAMMAR_CARD_CTA_INDEX = 2
export const SAVED = 'saved'
export const COPIED = 'copied'
export type Toast = typeof SAVED | typeof COPIED | null
export interface UndoReplacement {
  comment: CardContent
  undoEditorStartIndex: number
  lastContentState: ContentState
  undoType: UserInteractionStates
  undoListIndex?: number
  plagiarismScore?: number
}

export interface CheckTabsVisited {
  [category: string]: boolean
}

export interface WorkspaceState {
  id: string
  filename: string
  text?: string
  error?: Error
  maxFreeComments: number
  newUpload?: boolean
  lastTouchedEntityId?: string
  selectedEntityId?: string
  selectedSharedEntityId?: string
  focusedComponent?: string
  isRecheck?: boolean
  filters: string[]
  currentCheckCategory: CheckCategory
  initialCheckCategory: CheckCategory | null
  checkTabsVisited: CheckTabsVisited
  toast: Toast
  hasMaxFreeComments?: boolean
  checksToExpect?: CheckType[]
  grammarAnalyzeFailed: boolean
  plagiarismAnalyzeFailed: boolean
  checksRunning: boolean
  partialPlagiarismRunning: boolean
  partialPlagiarismFound: boolean
  creatingPaper: boolean
  undoReplacement?: UndoReplacement
  grammarCardCtaIndex: number
  confirmedLeaveModal?: boolean
  checksCancelled?: boolean
  inPaymentFlow?: boolean
}

export const initialState: WorkspaceState = {
  id: '',
  maxFreeComments: 5,
  filename: '',
  isRecheck: false,
  filters: [],
  currentCheckCategory: GRAMMAR,
  initialCheckCategory: null,
  checkTabsVisited: { [GRAMMAR]: false, [PLAGIARISM]: false, [EXPERT_CHECK]: false },
  toast: null,
  checksToExpect: [],
  grammarAnalyzeFailed: false,
  plagiarismAnalyzeFailed: false,
  checksRunning: false,
  partialPlagiarismRunning: false,
  partialPlagiarismFound: false,
  creatingPaper: true,
  undoReplacement: undefined,
  grammarCardCtaIndex: DEFAULT_GRAMMAR_CARD_CTA_INDEX,
  confirmedLeaveModal: false,
  checksCancelled: false,
  inPaymentFlow: false,
}

type Actions =
  | WorkspaceActions
  | DocumentActions
  | CheckActions
  | SessionActions
  | GlobalActions

export default (
  state: WorkspaceState = initialState,
  action: Actions
): WorkspaceState => {
  switch (action.type) {
    case getType(workspaceActions.paymentFlowStart): {
      return {
        ...state,
        inPaymentFlow: true,
      }
    }
    case getType(workspaceActions.paymentFlowEnd): {
      return {
        ...state,
        inPaymentFlow: false,
      }
    }
    case getType(workspaceActions.setCurrentCategory): {
      const { checkCategory } = action.payload
      return {
        ...state,
        currentCheckCategory: checkCategory,
        undoReplacement: undefined,
        checkTabsVisited: { ...state.checkTabsVisited, [checkCategory]: true },
      }
    }
    case getType(workspaceActions.setInitialCategory): {
      return {
        ...state,
        initialCheckCategory: action.payload.checkCategory,
      }
    }
    case getType(workspaceActions.showToast): {
      return {
        ...state,
        toast: action.payload.toast,
      }
    }
    case getType(workspaceActions.setHasMaxFreeComments): {
      return {
        ...state,
        hasMaxFreeComments: action.payload.value,
      }
    }
    case getType(workspaceActions.clearToast): {
      return {
        ...state,
        toast: null,
      }
    }
    case getType(documentActions.clearActiveCommentId):
      return {
        ...state,
        selectedEntityId: undefined,
        selectedSharedEntityId: undefined,
        focusedComponent: undefined,
      }
    case getType(documentActions.setActiveEntityKey):
      return {
        ...state,
        lastTouchedEntityId: action.payload.key,
        selectedEntityId: action.payload.key,
        selectedSharedEntityId: action.payload.key,
        focusedComponent: action.payload.component,
      }
    case getType(documentActions.setActiveCommentId):
      return {
        ...state,
        lastTouchedEntityId: action.payload.id,
        selectedEntityId: action.payload.id,
        selectedSharedEntityId: action.payload.sharedId,
        focusedComponent: action.payload.component,
      }
    case getType(documentActions.handleDocumentCreate): {
      return {
        ...state,
        id: action.payload.id,
      }
    }
    case getType(workspaceActions.setNewUpload): {
      return {
        ...state,
        newUpload: action.payload.isNew,
      }
    }
    case getType(documentActions.setDocument): {
      return {
        ...state,
        id: action.payload.id,
      }
    }
    case getType(checkActions.runChecks): {
      return {
        ...state,
        isRecheck: action.payload.isRecheck || false,
        grammarAnalyzeFailed: false,
        checksRunning: true,
        checksToExpect: [],
      }
    }
    case getType(documentActions.saveDocument.success): {
      return {
        ...state,
        isRecheck: false,
      }
    }
    case getType(documentActions.getPlainTextFail):
    case getType(documentActions.getDocument.failure): {
      return {
        ...state,
        error: {
          ...action.payload,
        },
      }
    }
    case getType(documentActions.setFilename): {
      return {
        ...state,
        filename: action.payload.name,
      }
    }

    case getType(sessionActions.loadFromLocalStorage): {
      const { filename, uploadId } = action.payload
      return {
        ...state,
        id: uploadId,
        filename,
      }
    }

    case getType(checkActions.setExpectedChecks): {
      return {
        ...state,
        checksToExpect: action.payload.checks as CheckType[],
      }
    }

    case getType(checkActions.pusherRunPlagiarismCheck): {
      return {
        ...state,
        plagiarismAnalyzeFailed: false,
      }
    }

    case getType(checkActions.checkStartFail): {
      return {
        ...state,
        plagiarismAnalyzeFailed:
          action.payload.plagiarism || state.plagiarismAnalyzeFailed,
        grammarAnalyzeFailed: action.payload.grammar || state.grammarAnalyzeFailed,
        checksRunning: false,
        checksCancelled: false,
        isRecheck: false,
      }
    }

    case getType(checkActions.checksComplete): {
      return {
        ...state,
        checksRunning: false,
        checksCancelled: false,
        isRecheck: false,
      }
    }

    case getType(checkActions.partialPlagiarismCheckStart): {
      return {
        ...state,
        partialPlagiarismRunning: true,
        partialPlagiarismFound: false,
      }
    }

    case getType(checkActions.partialPlagiarismCheckEnd): {
      return {
        ...state,
        partialPlagiarismRunning: false,
        partialPlagiarismFound: action.payload.plagiarismFound,
      }
    }
    case getType(documentActions.createPaper): {
      return {
        ...state,
        creatingPaper: true,
      }
    }
    case getType(documentActions.createPaperSuccess): {
      return {
        ...state,
        creatingPaper: false,
      }
    }
    case getType(workspaceActions.showUndoReplacement): {
      return {
        ...state,
        undoReplacement: { ...action.payload.undo } as UndoReplacement,
      }
    }
    case getType(workspaceActions.clearUndoReplacement): {
      return {
        ...state,
        undoReplacement: undefined,
      }
    }

    case getType(checkActions.checksCancelled): {
      return {
        ...state,
        checksRunning: false,
        checksCancelled: true,
      }
    }

    case getType(documentActions.saveDocument.request): {
      return {
        ...state,
        initialCheckCategory: null,
      }
    }
    case getType(resetState): {
      return initialState
    }
    case getType(workspaceActions.setGrammarCardCtaIndex): {
      return {
        ...state,
        grammarCardCtaIndex: action.payload.ctaIndex,
      }
    }
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/state/workspace/reducer.ts